/**
 * Atlas Web — shared UI primitives.
 *
 * Small, dependency-free building blocks used by every page: Card,
 * Button, Badge, StatusBadge, PageHeader, EmptyState, Skeleton,
 * Field/Input/Select, BlockedActionExplanation (the "why is this
 * blocked?" panel mandated by the UX spec), and a toolbar.
 */
import {
  createContext,
  isValidElement,
  useContext,
  useId,
  useState,
  type AriaAttributes,
  type ReactNode,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
} from "react";
import { clsx } from "clsx";
import { useTranslation } from "@/lib/i18n";
import { statusLabel } from "@/lib/i18n/records";

/* ---- Card ------------------------------------------------------------- */
export function Card({
  children,
  className,
  title,
  actions,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
  actions?: ReactNode;
}) {
  return (
    <section className={clsx("atlas-card", className)}>
      {(title || actions) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBlockEnd: 12,
          }}
        >
          {title && (
            <h2 className="atlas-h2" style={{ margin: 0, flex: 1 }}>
              {title}
            </h2>
          )}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/* ---- Button ------------------------------------------------------------ */
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md";
}
export function Button({
  variant = "secondary",
  size = "md",
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={clsx(
        "atlas-btn",
        variant === "primary" && "atlas-btn--primary",
        variant === "danger" && "atlas-btn--danger",
        size === "sm" && "atlas-btn--sm",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ---- Badge ------------------------------------------------------------- */
export type BadgeTone =
  "default" | "success" | "warning" | "danger" | "primary";
export function Badge({
  tone = "default",
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "atlas-badge",
        tone === "success" && "atlas-badge--success",
        tone === "warning" && "atlas-badge--warning",
        tone === "danger" && "atlas-badge--danger",
        tone === "primary" && "atlas-badge--primary",
        className,
      )}
    >
      {children}
    </span>
  );
}

const STATUS_TONES: Record<string, BadgeTone> = {
  Posted: "success",
  Approved: "success",
  Active: "success",
  Paid: "success",
  Completed: "success",
  Confirmed: "success",
  Released: "primary",
  Open: "primary",
  Matched: "success",
  Draft: "default",
  Pending: "warning",
  Submitted: "warning",
  Sent: "warning",
  InProgress: "warning",
  Closed: "default",
  Suspended: "danger",
  Blocked: "danger",
  Overdue: "danger",
  Rejected: "danger",
  Cancelled: "danger",
  Failed: "danger",
};

/**
 * A stored status as a coloured word. With `type` (the record type the status
 * belongs to, `SalesInvoice`), the word is the glossary's in the active
 * language, agreeing with the record (lib/i18n/records.ts); without it, the
 * value is shown as stored, which only screens not yet converted still do.
 */
export function StatusBadge({
  status,
  type,
}: {
  status: string;
  type?: string;
}) {
  const { t } = useTranslation();
  return (
    <Badge tone={STATUS_TONES[status] ?? "default"}>
      {type ? statusLabel(t, type, status) : status}
    </Badge>
  );
}

/* ---- Page header -------------------------------------------------------- */
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumb,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  breadcrumb?: string[];
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-4">
      {breadcrumb && breadcrumb.length > 0 && (
        <nav
          className="atlas-muted mb-2"
          style={{ fontSize: 13 }}
          aria-label={t("ui.breadcrumb")}
        >
          {breadcrumb.map((crumb, i) => (
            <span key={crumb}>
              {i > 0 && <span style={{ marginInline: 6 }}>/</span>}
              {crumb}
            </span>
          ))}
        </nav>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minInlineSize: 240 }}>
          <h1 className="atlas-page-title">{title}</h1>
          {subtitle && <p className="atlas-page-subtitle">{subtitle}</p>}
        </div>
        {actions && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- Empty state -------------------------------------------------------- */
export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message?: string;
  action?: ReactNode;
}) {
  return (
    <div className="atlas-empty">
      <div className="atlas-empty__title">{title}</div>
      {message && <p style={{ fontSize: 14 }}>{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* ---- Skeleton ----------------------------------------------------------- */
export function Skeleton({
  rows = 6,
  height = 16,
}: {
  rows?: number;
  height?: number;
}) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="atlas-skeleton"
          style={{ height, marginBlockEnd: 10 }}
        />
      ))}
    </div>
  );
}

/* ---- Form field --------------------------------------------------------- */
/**
 * What a Field tells the control inside it. The label used to be a bare
 * <label> with no `for` (D21), so no input in the product had an accessible
 * name: a screen reader announced "edit text", and nothing could find the
 * sign-in fields by their label.
 */
interface FieldControl {
  /** The control's id; the Field's <label htmlFor> points at it. */
  id: string;
  /** The visible hint or error, for aria-describedby. */
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

/**
 * Read by Input and Select. A new kind of control placed inside a Field must
 * read it too and put `id`, `aria-describedby` and `aria-invalid` on the
 * element that takes the input, or the Field's label points at nothing.
 */
const FieldContext = createContext<FieldControl | null>(null);

function joinIds(...ids: Array<string | undefined>): string | undefined {
  const joined = ids.filter(Boolean).join(" ");
  return joined === "" ? undefined : joined;
}

/** Field wiring merged under what the control was given explicitly. */
function fieldControlProps(
  field: FieldControl | null,
  own: AriaAttributes & { id?: string; required?: boolean },
) {
  if (!field) return {};
  return {
    id: own.id ?? field.id,
    "aria-describedby": joinIds(own["aria-describedby"], field.describedBy),
    "aria-invalid": own["aria-invalid"] ?? (field.invalid || undefined),
    // A Field marked required whose control is not (`required` blocks native
    // submission, which some forms validate themselves) is still announced
    // as required.
    "aria-required":
      own["aria-required"] ??
      (field.required && !own.required ? true : undefined),
  };
}

/**
 * A labelled form field. Its label is linked to the control inside it: the
 * control gets the Field's `id` (generated when none is given), and its hint
 * or error through aria-describedby; an error also sets aria-invalid. Input
 * and Select pick this up on their own. The asterisk on a required field is
 * hidden from assistive technology, so the field is named by its label alone
 * and its required state comes from the control.
 *
 * Give an explicit id to the Field (`id`) or to its single control, not to a
 * control nested deeper.
 */
export function Field({
  label,
  required,
  hint,
  children,
  error,
  id,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
  error?: string;
  id?: string;
}) {
  const generated = useId();
  const childId =
    isValidElement<{ id?: unknown }>(children) &&
    typeof children.props.id === "string"
      ? children.props.id
      : undefined;
  const controlId = id ?? childId ?? `${generated}control`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const showHint = Boolean(hint) && !error;
  const control: FieldControl = {
    id: controlId,
    describedBy: error ? errorId : showHint ? hintId : undefined,
    invalid: Boolean(error),
    required: Boolean(required),
  };
  return (
    <div className="atlas-field">
      <label className="atlas-label" htmlFor={controlId}>
        {label}
        {/* The gap is a margin, not a space: a text space outside the hidden
            span stayed in the name, and Chrome announced "Company name ". */}
        {required && (
          <span
            aria-hidden="true"
            style={{
              color: "var(--atlas-danger)",
              marginInlineStart: "0.25em",
            }}
          >
            *
          </span>
        )}
      </label>
      <FieldContext.Provider value={control}>{children}</FieldContext.Provider>
      {showHint && (
        <div
          id={hintId}
          className="atlas-muted"
          style={{ fontSize: 12, marginBlockStart: 4 }}
        >
          {hint}
        </div>
      )}
      {error && (
        <div
          id={errorId}
          style={{
            color: "var(--atlas-danger)",
            fontSize: 12,
            marginBlockStart: 4,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  const field = useContext(FieldContext);
  return (
    <input
      className="atlas-input"
      {...props}
      {...fieldControlProps(field, props)}
    />
  );
}

/**
 * A required <select> still on its empty placeholder is invalid to the
 * browser from the moment it renders, and Chrome exposes that: its
 * accessibility tree reported Country, Currency, Time zone and Language as
 * invalid on a freshly opened workspace form, so a screen reader announced
 * each one as "invalid entry" before the person had done anything. (Chrome
 * holds that back for a text input until someone has used it.)
 *
 * So a required Select says aria-invalid="false" until something is actually
 * wrong: its Field's error, or the browser's own validation reporting it (the
 * `invalid` event, fired when a form that validates natively is submitted).
 * From then on the browser's state is shown, as it is for an input once it
 * has been used.
 */
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  const field = useContext(FieldContext);
  const [reportedByBrowser, setReportedByBrowser] = useState(false);
  const { onInvalid } = props;
  const quiet =
    Boolean(props.required) &&
    props["aria-invalid"] === undefined &&
    !field?.invalid &&
    !reportedByBrowser;
  return (
    <select
      className="atlas-select"
      {...props}
      {...fieldControlProps(field, props)}
      {...(quiet ? { "aria-invalid": false } : {})}
      onInvalid={(e) => {
        setReportedByBrowser(true);
        onInvalid?.(e);
      }}
    />
  );
}

/* ---- Blocked action explanation ----------------------------------------- */
export interface BlockReason {
  code: string;
  message: string;
  resolutionPath?: string;
  permission?: string;
  limit?: string;
}
export function BlockedActionExplanation({
  title,
  reasons,
}: {
  title: string;
  reasons: BlockReason[];
}) {
  const { t } = useTranslation();
  return (
    <div className="atlas-blocked" role="alert" aria-live="assertive">
      <h3 className="atlas-blocked__title">{title}</h3>
      <ol style={{ margin: 0, paddingInlineStart: 20 }}>
        {reasons.map((r) => (
          <li key={r.code}>
            <strong>{r.message}</strong>
            {r.resolutionPath && (
              // Body colour, not the muted grey: grey on the panel's red tint
              // is about 4:1, under the 4.5:1 small text needs (D60, axe
              // color-contrast on /app/finance/bank-reconciliation).
              <div style={{ fontSize: 13, color: "var(--atlas-text)" }}>
                {t("ui.howToFix")} {r.resolutionPath}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ---- Confirm dialog ----------------------------------------------------- */
/**
 * The one confirmation (safety-net §4.2; document-editor §9 item 11): a
 * native <dialog> opened with showModal(), which traps focus and closes on
 * Escape on its own, naming the object and the consequence. Cancel takes the
 * first focus, so Enter on an opened dialog never confirms a destructive act.
 * A refusal from the API is shown inside it, and the person can try again or
 * cancel. UI-HONEST builds the first of it for the session revokes;
 * SN-11 adds the reason box and the warnings beside it.
 *
 * Used through `useConfirmedAction` (components/ConfirmedAction.tsx), which
 * the SG6 guard requires for every destructive call.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  tone = "danger",
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  tone?: "danger" | "primary";
  busy?: boolean;
  /** The API's refusal of the last attempt, shown in words. */
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const titleId = `${id}title`;
  const bodyId = `${id}body`;
  const open = (el: HTMLDialogElement | null) => {
    if (!el || el.open) return;
    // The test DOM has no showModal; a browser always does.
    if (typeof el.showModal === "function") el.showModal();
    else el.setAttribute("open", "");
  };
  return (
    <dialog
      ref={open}
      className="atlas-card"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      onCancel={(event) => {
        // Escape: keep React the owner of whether the dialog is open.
        event.preventDefault();
        if (!busy) onCancel();
      }}
      style={{
        maxInlineSize: 440,
        border: "1px solid var(--atlas-border)",
        color: "var(--atlas-text)",
        background: "var(--atlas-surface)",
      }}
    >
      <h2 id={titleId} className="atlas-h2" style={{ marginBlockStart: 0 }}>
        {title}
      </h2>
      <p id={bodyId} style={{ fontSize: 14 }}>
        {body}
      </p>
      {error && (
        <div
          role="alert"
          style={{ color: "var(--atlas-danger)", fontSize: 13, marginBlock: 8 }}
        >
          {error}
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginBlockStart: 16,
        }}
      >
        <Button autoFocus disabled={busy} onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button
          variant={tone === "danger" ? "danger" : "primary"}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? t("confirmDialog.working") : confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}

/* ---- Toolbar ------------------------------------------------------------ */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="atlas-toolbar mb-4">{children}</div>;
}

/* ---- KPI stat ------------------------------------------------------------ */
export function StatCard({
  label,
  value,
  detail,
  meta,
  tone,
}: {
  label: string;
  value: ReactNode;
  /** Beneath the value, before `meta`: e.g. the same figure in other currencies. */
  detail?: ReactNode;
  meta?: string;
  tone?: "positive" | "negative";
}) {
  return (
    <Card>
      <div className="atlas-stat__label">{label}</div>
      <div className="atlas-stat__value">{value}</div>
      {detail}
      {meta && (
        <div
          className={clsx(
            "atlas-stat__meta",
            tone === "positive" && "atlas-stat__meta--positive",
            tone === "negative" && "atlas-stat__meta--negative",
          )}
        >
          {meta}
        </div>
      )}
    </Card>
  );
}

/* ---- Tabs --------------------------------------------------------------- */
/**
 * A tab: a string is its own id and label; an object keeps the id (what
 * `active` and `onChange` carry) apart from the label, so a screen decides
 * by a stable id rather than by text that changes with the language.
 */
export type TabItem = string | { id: string; label: string };

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: TabItem[];
  active: string;
  onChange: (tab: string) => void;
}) {
  return (
    <div className="atlas-tabs mb-4" role="tablist">
      {tabs.map((tab) => {
        const id = typeof tab === "string" ? tab : tab.id;
        const label = typeof tab === "string" ? tab : tab.label;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active === id}
            className={clsx("atlas-tab", active === id && "is-active")}
            onClick={() => onChange(id)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ---- Simple table -------------------------------------------------------- */
/** Read one cell by column key. The cast is confined here so callers keep their
 *  own precise row types instead of having to widen them for the table. */
function cellOf<T extends object>(row: T, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

export function DataTable<T extends object>({
  columns,
  rows,
  keyField = "id",
  onRowClick,
}: {
  columns: Array<{
    key: string;
    label: string;
    numeric?: boolean;
    render?: (row: T) => ReactNode;
  }>;
  rows: T[];
  keyField?: string;
  onRowClick?: (row: T) => void;
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="atlas-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={col.numeric ? "is-numeric" : undefined}
                scope="col"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={String(cellOf(row, keyField) ?? JSON.stringify(row))}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: "pointer" } : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={col.numeric ? "is-numeric" : undefined}
                >
                  {col.render
                    ? col.render(row)
                    : ((cellOf(row, col.key) as ReactNode) ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
